import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

export const Navbar = ({ phone }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onLanding = location.pathname === "/";
  const { t, lang, setLang } = useLang();

  const links = [
    { label: t("nav.home"), to: "hero" },
    { label: t("nav.services"), to: "leistungen" },
    { label: t("nav.packages"), to: "pakete" },
    { label: t("nav.about"), to: "ueber-uns" },
    { label: t("nav.why"), to: "warum-wir" },
    { label: t("nav.gallery"), to: "galerie" },
    { label: t("nav.reviews"), to: "bewertungen" },
    { label: t("nav.contact"), to: "kontakt" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const light = scrolled || !onLanding;

  const LangToggle = ({ className = "" }) => (
    <div data-testid="lang-toggle" className={`flex items-center rounded-full border overflow-hidden text-xs ${light ? "border-slate-300" : "border-white/40"} ${className}`}>
      {["de", "en"].map((l) => (
        <button key={l} data-testid={`lang-${l}`} onClick={() => setLang(l)}
          className={`px-3 py-1.5 font-semibold transition-colors ${lang === l ? "bg-blue-600 text-white" : light ? "text-slate-600 hover:text-slate-900" : "text-white/90 hover:text-white"}`}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );

  return (
    <nav data-testid="navbar" className={`fixed top-0 w-full z-50 transition-all duration-300 ${light ? "backdrop-blur-xl bg-white/85 border-b border-slate-200 py-3" : "bg-transparent py-5"}`}>
      <div className="container-x flex items-center justify-between">
        <Link to="/" data-testid="logo" className="flex flex-col leading-none">
          <span className={`font-heading font-extrabold text-lg sm:text-xl tracking-tight ${light ? "text-slate-900" : "text-white"}`}>BERISHA</span>
          <span className="text-[10px] sm:text-xs text-blue-600 tracking-[0.3em] uppercase">Autoaufbereitung</span>
        </Link>

        {onLanding && (
          <div className="hidden lg:flex items-center gap-6">
            {links.map((l) => (
              <button key={l.to} data-testid={`nav-${l.to}`} onClick={() => scrollTo(l.to)}
                className={`text-sm transition-colors duration-200 relative group ${light ? "text-slate-600 hover:text-slate-900" : "text-white/90 hover:text-white"}`}>
                {l.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600 group-hover:w-full transition-all duration-300" />
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-3">
          <LangToggle className="hidden sm:flex" />
          <a href={`tel:${phone?.replace(/\s/g, "")}`} data-testid="nav-call" className="btn-blue !px-5 !py-2.5 text-sm hidden sm:inline-flex">
            <Phone className="w-4 h-4" /> {t("nav.call")}
          </a>
          {onLanding && (
            <button className={light ? "lg:hidden text-slate-900" : "lg:hidden text-white"} data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)}>
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          )}
        </div>
      </div>

      {open && onLanding && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-slate-200 mt-3">
          <div className="container-x py-4 flex flex-col gap-1">
            {links.map((l) => (
              <button key={l.to} data-testid={`mobile-nav-${l.to}`} onClick={() => scrollTo(l.to)}
                className="text-left py-3 text-slate-700 hover:text-blue-600 transition-colors border-b border-slate-100">
                {l.label}
              </button>
            ))}
            <div className="flex items-center justify-between mt-4">
              <LangToggle />
              <a href={`tel:${phone?.replace(/\s/g, "")}`} className="btn-blue !px-5 !py-2.5 text-sm">
                <Phone className="w-4 h-4" /> {t("nav.callNow")}
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
